import { AlertMessage, ConfirmDialog, Spinner } from "@/components/reusable";
import {
  buildMandatoryRule,
  buildNoDuplicatesRule,
  columnIdsByName,
  columnNamesById,
  findDuplicateNameColumnIds,
  isAutoTypeCheckRule,
  isDuplicatesRule,
  isMandatoryRule,
  isSchemaNameTaken,
  reconcileMissingFlagRules,
  renameColumnInRuleParameters,
  renderRuleFormula,
  ruleReferencesUnresolvedColumn,
  toColumnOptions,
} from "@/lib/schemaBuilderHelpers";
import { AddRuleModal } from "@/components/schema-builder/AddRuleModal";
import { ColumnEditorDrawer } from "@/components/schema-builder/ColumnEditorDrawer";
import { RuleConfigurationModal } from "@/components/schema-builder/RuleConfigurationModal";
import { ValidateDraftModal } from "@/components/schema-builder/ValidateDraftModal";
import { SchemaBuilderActivityTab } from "@/components/schema-builder/SchemaBuilderActivityTab";
import { SchemaBuilderColumnsTab } from "@/components/schema-builder/SchemaBuilderColumnsTab";
import { SchemaBuilderReferenceListsTab } from "@/components/schema-builder/SchemaBuilderReferenceListsTab";
import { SchemaBuilderRulesTab } from "@/components/schema-builder/SchemaBuilderRulesTab";
import {
  createSchemaFromFile,
  createSchemaFromTemplate,
  finalizeNewSchema,
  getRuleTemplates,
  getSchemaActivity,
  getSchemaDetail,
  getSchemaRules,
  listSchemas,
  mapValidationResult,
  publishSchema,
  saveAndValidate,
  saveDraft,
} from "@/services/schemaBuilderApi";
import type { SaveDraftPayload, SaveDraftResult } from "@/services/schemaBuilderApi";
import { invalidateProvidersCache } from "@/services/providersApi";
import type {
  SchemaActivityEvent,
  SchemaColumn,
  SchemaMappedFile,
  SchemaRule,
  SchemaRuleCategory,
  SchemaRulePreset,
  ValidationResult,
} from "@/types/schemaBuilder";
import {
  clearEditSession,
  clearSchemaActivityLog,
  deleteCustomSchema,
  getCustomSchemas,
  getEditSession,
  getSchemaActivityLog,
  saveCustomSchema,
  saveEditSession,
  saveSchemaActivityLog,
  upsertSchemaInCache,
} from "@/lib/schemaBuilderStorage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSchemaPermissions } from "@/hooks/useSchemaPermissions";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const TABS = [
  { label: "Columns", value: "columns" },
  { label: "Cross Column Rules", value: "rules" },
  { label: "Reference Lists", value: "reference-lists" },
  { label: "Activity", value: "activity" },
] as const;

type ActiveTab = (typeof TABS)[number]["value"];

interface ConflictCluster {
  key: string;
  titles: string[];
  reason?: string;
}

function conflictDisplayLabel(rule: SchemaRule): string {
  return rule.formula?.trim() || rule.title;
}

// Stable group key so converse cross-column rules (same columns, opposite order)
// land in one group instead of two ("A, B" vs "B, A").
function conflictGroupKey(rule: SchemaRule, colNamesById: Record<string, string>): string {
  const names = rule.columnIds
    .map((id) => colNamesById[id])
    .filter((name): name is string => Boolean(name));
  if (names.length === 0) return "Schema-level rules";
  return [...names].sort((a, b) => a.localeCompare(b)).join(", ");
}

// Rules that conflict with each other share the same backend-generated reason text —
// cluster on that so a pair renders as one entry instead of repeating the reason twice.
function clusterConflictingRules(rules: SchemaRule[]): ConflictCluster[] {
  const byReason = new Map<string, SchemaRule[]>();
  const clusters: ConflictCluster[] = [];
  for (const rule of rules) {
    if (!rule.conflictReason) {
      clusters.push({ key: rule.id, titles: [conflictDisplayLabel(rule)] });
      continue;
    }
    const existing = byReason.get(rule.conflictReason);
    if (existing) existing.push(rule);
    else byReason.set(rule.conflictReason, [rule]);
  }
  for (const [reason, grouped] of byReason) {
    clusters.push({
      key: grouped.map((r) => r.id).join("+"),
      titles: grouped.map(conflictDisplayLabel),
      reason,
    });
  }
  return clusters;
}

// Rule formulas/titles are bold; the connecting words between them are italic, not bold.
function ConflictTitles({ titles }: { titles: string[] }) {
  if (titles.length <= 1) {
    return <span className="font-semibold">{titles[0] ?? ""}</span>;
  }
  if (titles.length === 2) {
    return (
      <div className="space-y-0.5">
        <div className="font-semibold">{titles[0]}</div>
        <div className="italic">conflicts with</div>
        <div className="font-semibold">{titles[1]}</div>
      </div>
    );
  }
  const rest = titles.slice(0, -1);
  const last = titles[titles.length - 1];
  return (
    <>
      {rest.map((title, i) => (
        <span key={title}>
          <span className="font-semibold">{title}</span>
          {i < rest.length - 1 ? ", " : " "}
        </span>
      ))}
      <span className="italic">and</span> <span className="font-semibold">{last}</span>{" "}
      <span className="italic">conflict with each other</span>
    </>
  );
}

export function SchemaBuilderEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { schemaId } = useParams();
  const currentUser = useCurrentUser();
  const { canPublish } = useSchemaPermissions();

  const source = location.state?.source;
  const isScratchSchema = source === "scratch";
  const isTemplateSchema = source === "template";
  const cameFromUpload = source === "upload";
  const cameFromEdit = source === "edit";
  const cameFromCreate = source === "create" || isScratchSchema || isTemplateSchema;

  const schemaName = location.state?.schemaName ?? "Untitled Schema";
  const fileId: string | null = location.state?.fileId ?? null;
  const providerId: string | null = location.state?.providerId ?? null;
  const fileName =
    location.state?.fileName ??
    (isTemplateSchema && location.state?.templateName
      ? `Preset - ${location.state.templateName}`
      : "");

  const _sessionName = (() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.name;
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.name;
  })();
  const _sessionDescription = (() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.description;
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.description;
  })();

  const _nameInit = _sessionName ?? schemaName;
  const [nameField, setNameField] = useState({
    value: _nameInit,
    draft: _nameInit,
    isEditing: false,
  });
  const editableName = nameField.value;
  const nameDraft = nameField.draft;
  const isEditingName = nameField.isEditing;
  const [nameError, setNameError] = useState("");
  const [isCheckingName, setIsCheckingName] = useState(false);

  const _descInit = _sessionDescription ?? location.state?.schemaDescription ?? "";
  const [descField, setDescField] = useState({
    value: _descInit,
    draft: _descInit,
    isEditing: false,
  });
  const editableDescription = descField.value;
  const descriptionDraft = descField.draft;
  const isEditingDescription = descField.isEditing;

  const [apiSchemaId, setApiSchemaId] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(() => {
    // Skip the spinner for flows we can hydrate synchronously from localStorage
    if (isScratchSchema) return false;
    if (cameFromEdit && schemaId && getEditSession(schemaId)) return false;
    return true;
  });
  const [initError, setInitError] = useState<string | null>(null);
  const [initWarnings, setInitWarnings] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
  const [saveConflicts, setSaveConflicts] = useState<SchemaRule[]>([]);
  const [showValidateModal, setShowValidateModal] = useState(false);

  const originalColumnsRef = useRef<SchemaColumn[]>([]);
  const originalRulesRef = useRef<SchemaRule[]>([]);
  const originalReferenceListsRef = useRef<{ id: string; name: string; values: string[] }[]>([]);
  const sessionKeyRef = useRef<string | null>(null);
  const isDirtyRef = useRef(false);

  const [rulePresets, setRulePresets] = useState<SchemaRulePreset[]>([]);

  // Load rule templates once (cached in schemaBuilderApi module level)
  useEffect(() => {
    void getRuleTemplates()
      .then(setRulePresets)
      .catch(() => {});
  }, []);

  const [columns, setColumns] = useState<SchemaColumn[]>(() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.columns ?? [];
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.columns ?? [];
    return [];
  });
  const [localUploadedFiles, setLocalUploadedFiles] = useState<SchemaMappedFile[]>(() =>
    cameFromUpload && fileName
      ? [
          {
            fileName,
            mappedAt: new Date().toISOString(),
            uploadedBy: currentUser?.userEmail ?? "You",
          },
        ]
      : []
  );

  const [localActivity, setLocalActivity] = useState<SchemaActivityEvent[]>(() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.localActivity ?? [];
    if (cameFromEdit && schemaId) {
      const session = getEditSession(schemaId);
      // An active session already carries full history; otherwise fall back to the
      // persisted log from the last Save Draft.
      return session?.localActivity ?? getSchemaActivityLog(schemaId);
    }
    if (cameFromUpload && fileName) {
      return [
        {
          id: `local-${Date.now()}`,
          type: "file_uploaded" as const,
          actor: currentUser?.userEmail ?? "You",
          timestamp: new Date().toISOString(),
          detail: fileName,
        },
      ];
    }
    return [];
  });
  const [rules, setRules] = useState<SchemaRule[]>(() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.rules ?? [];
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.rules ?? [];
    return [];
  });
  const [referenceLists, setReferenceLists] = useState<
    { id: string; name: string; usedInColumnIds: string[]; itemCount: number; values: string[] }[]
  >(() => {
    if (isScratchSchema) return getEditSession(`scratch:${schemaName}`)?.referenceLists ?? [];
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.referenceLists ?? [];
    return [];
  });
  const [referenceListValuesByName, setReferenceListValuesByName] = useState<
    Record<string, string[]>
  >(() => {
    if (isScratchSchema)
      return getEditSession(`scratch:${schemaName}`)?.referenceListValuesByName ?? {};
    if (cameFromEdit && schemaId) return getEditSession(schemaId)?.referenceListValuesByName ?? {};
    return {};
  });

  useEffect(() => {
    const init = async () => {
      try {
        if (cameFromEdit && schemaId) {
          sessionKeyRef.current = schemaId;
          setApiSchemaId(schemaId);
          const session = getEditSession(schemaId);
          if (session) {
            // Already rendered from localStorage — background-fetch for originalColumnsRef/Rules
            void (async () => {
              try {
                const [detail, activity] = await Promise.all([
                  getSchemaDetail(schemaId),
                  getSchemaActivity(schemaId),
                ]);
                const colNameById = Object.fromEntries(
                  detail.columns.map((c) => [c.fieldId, c.name])
                );
                const refListNameById = Object.fromEntries(
                  detail._referenceLists.map((rl) => [rl.id, rl.name])
                );
                const schemaRules = await getSchemaRules(schemaId, colNameById, refListNameById);
                originalColumnsRef.current = detail.columns;
                originalRulesRef.current = schemaRules;
                originalReferenceListsRef.current = detail._referenceLists.map((rl) => ({
                  id: rl.id,
                  name: rl.name,
                  values: rl.values,
                }));
                // Prefer backend columns when the session has fewer — covers a session
                // saved before the initial fetch completed (e.g. mid-reload).
                if (detail.columns.length > (session.columns?.length ?? 0)) {
                  setColumns(detail.columns);
                }
                // Backfill any missing mandatory/unique rules (see reconcileMissingFlagRules).
                const missingFlagRules = reconcileMissingFlagRules(detail.columns, schemaRules);
                setRules(
                  missingFlagRules.length > 0 ? [...schemaRules, ...missingFlagRules] : schemaRules
                );
                // Prefer backend reference lists only when it has strictly more than the
                // session (session is behind) — otherwise local additions aren't saved yet.
                if (detail._referenceLists.length > (session.referenceLists?.length ?? 0)) {
                  setReferenceLists(detail._referenceLists);
                  setReferenceListValuesByName(detail._referenceListValuesByName);
                }
                setLocalUploadedFiles(
                  activity
                    .filter((e) => e.type === "file_uploaded")
                    .map((e) => ({
                      fileName: e.detail ?? "",
                      mappedAt: e.timestamp,
                      uploadedBy: e.actor,
                    }))
                );
              } catch {
                // Best-effort — refs stay [], Save Draft will re-add everything
              }
            })();
          } else {
            const [detail, activity] = await Promise.all([
              getSchemaDetail(schemaId),
              getSchemaActivity(schemaId),
            ]);
            const colNameById = Object.fromEntries(detail.columns.map((c) => [c.fieldId, c.name]));
            const refListNameById = Object.fromEntries(
              detail._referenceLists.map((rl) => [rl.id, rl.name])
            );
            const schemaRules = await getSchemaRules(schemaId, colNameById, refListNameById);
            originalColumnsRef.current = detail.columns;
            originalRulesRef.current = schemaRules;
            originalReferenceListsRef.current = detail._referenceLists.map((rl) => ({
              id: rl.id,
              name: rl.name,
              values: rl.values,
            }));
            setColumns(detail.columns);
            setDescField({
              value: detail.description ?? "",
              draft: detail.description ?? "",
              isEditing: false,
            });
            // localActivity already loaded from persistent log via lazy initializer.
            const missingFlagRules = reconcileMissingFlagRules(detail.columns, schemaRules);
            setRules(
              missingFlagRules.length > 0 ? [...schemaRules, ...missingFlagRules] : schemaRules
            );
            setReferenceLists(detail._referenceLists);
            setReferenceListValuesByName(detail._referenceListValuesByName);
            setLocalUploadedFiles(
              activity
                .filter((e) => e.type === "file_uploaded")
                .map((e) => ({
                  fileName: e.detail ?? "",
                  mappedAt: e.timestamp,
                  uploadedBy: e.actor,
                }))
            );
          }
        } else if (isScratchSchema) {
          // Already rendered from localStorage lazy init — just wire up the session key
          sessionKeyRef.current = `scratch:${schemaName}`;
          if (!getEditSession(`scratch:${schemaName}`)?.localActivity?.length) {
            recordLocalActivity({
              type: "schema_created",
              actor: currentUser?.userEmail ?? "You",
              timestamp: new Date().toISOString(),
              detail: "from Scratch",
            });
          }
        } else if (isTemplateSchema && schemaId) {
          const detail = await createSchemaFromTemplate(schemaId, {
            name: schemaName,
            ...(providerId ? { provider_id: providerId } : {}),
          });
          const colNameById = Object.fromEntries(detail.columns.map((c) => [c.fieldId, c.name]));
          const refListNameById = Object.fromEntries(
            detail._referenceLists.map((rl) => [rl.id, rl.name])
          );
          const schemaRules = await getSchemaRules(detail.id, colNameById, refListNameById);
          setApiSchemaId(detail.id);
          originalColumnsRef.current = detail.columns;
          originalRulesRef.current = schemaRules;
          sessionKeyRef.current = detail.id;
          navigate(`/schema-builder/${detail.id}`, {
            replace: true,
            state: { source: "edit", schemaName: detail.name },
          });
          const session = getEditSession(detail.id);
          setColumns(session?.columns ?? detail.columns);
          setLocalActivity([
            {
              id: `local-${Date.now()}`,
              type: "schema_created",
              actor: currentUser?.userEmail ?? "You",
              timestamp: new Date().toISOString(),
              detail: "via Template",
            },
            ...(session?.localActivity ?? []),
          ]);
          // Duplicating/templating a schema intentionally carries over columns and reference
          // lists but not rules -- don't backfill mandatory/unique rules here either.
          setRules(session?.rules ?? schemaRules);
          setReferenceLists(detail._referenceLists);
          setReferenceListValuesByName(detail._referenceListValuesByName);
        } else if (cameFromUpload) {
          const detail = await createSchemaFromFile(fileId, {
            name: schemaName,
            ...(providerId ? { provider_id: providerId } : {}),
          });
          setApiSchemaId(detail.id);
          originalColumnsRef.current = detail.columns;
          originalRulesRef.current = [];
          sessionKeyRef.current = detail.id;
          navigate(`/schema-builder/${detail.id}`, {
            replace: true,
            state: { source: "edit", schemaName: detail.name },
          });
          const session = getEditSession(detail.id);
          const freshColumns = detail.columns;
          // Backfill any missing mandatory/unique rules from upload-time inference.
          const freshRules = reconcileMissingFlagRules(freshColumns, []);
          setColumns(session?.columns ?? freshColumns);
          setRules(session?.rules ?? freshRules);
          setLocalActivity([
            {
              id: `local-${Date.now()}`,
              type: "schema_created",
              actor: currentUser?.userEmail ?? "You",
              timestamp: new Date().toISOString(),
              detail: "via Upload",
            },
            ...(session?.localActivity ?? []),
          ]);
          setReferenceLists(detail._referenceLists);
          setReferenceListValuesByName(detail._referenceListValuesByName);
          if (detail.warnings.length > 0) setInitWarnings(detail.warnings);
        }
      } catch (err) {
        setInitError((err as Error).message);
      } finally {
        setIsInitializing(false);
      }
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitializing) return;
    const key = sessionKeyRef.current;
    if (!key) return;
    saveEditSession(key, {
      columns,
      localActivity,
      referenceListValuesByName,
      referenceLists,
      rules,
      name: editableName,
      description: editableDescription,
    });

    // Keep the localStorage schema summary in sync so the list page reflects live
    // conflicts without needing a Save Draft first.
    if (apiSchemaId) {
      const existing = getCustomSchemas().find((s) => s.id === apiSchemaId);
      if (existing) {
        saveCustomSchema({
          ...existing,
          name: editableName,
          description: editableDescription,
          totalColumns: columns.length,
          rules: rules.length,
          conflicts: conflictCount,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, localActivity, referenceListValuesByName, referenceLists, rules, isInitializing]);

  function recordLocalActivity(event: Omit<SchemaActivityEvent, "id">) {
    setLocalActivity((prev) => [{ ...event, id: `local-${Date.now()}` }, ...prev]);
  }

  // Clears the name-related save error once the name is fixed.
  useEffect(() => {
    if (nameError) return;
    setSaveError((prev) =>
      prev === "Fix the schema name above before saving — the draft wasn't saved." ? null : prev
    );
  }, [nameError]);

  // Returns false if the name is a duplicate.
  async function commitNameEdit(): Promise<boolean> {
    const newName = nameDraft.trim() || editableName;

    if (newName === editableName) {
      setNameError("");
      setNameField({ value: newName, draft: newName, isEditing: false });
      return true;
    }

    setIsCheckingName(true);
    try {
      const schemas = await listSchemas(true);
      if (isSchemaNameTaken(newName, schemas, apiSchemaId ?? undefined)) {
        setNameError("A schema with this name already exists. Please choose a different name.");
        return false;
      }
    } finally {
      setIsCheckingName(false);
    }

    setNameError("");
    setNameField({ value: newName, draft: newName, isEditing: false });
    isDirtyRef.current = true;
    recordLocalActivity({
      type: "schema_renamed",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: newName,
    });
    return true;
  }

  async function handleCommitNameEdit() {
    await commitNameEdit();
  }

  function handleCommitDescriptionEdit() {
    setDescField((prev) => ({ value: prev.draft, draft: prev.draft, isEditing: false }));
    if (descriptionDraft !== editableDescription) {
      isDirtyRef.current = true;
    }
  }

  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const [activeTab, setActiveTab] = useState<ActiveTab>("columns");
  const [selectedColumn, setSelectedColumn] = useState<SchemaColumn | null>(null);
  const [columnToDelete, setColumnToDelete] = useState<SchemaColumn | null>(null);
  const [creatingColumnDraft, setCreatingColumnDraft] = useState<SchemaColumn | null>(null);

  const [showSchemaAddRuleModal, setShowSchemaAddRuleModal] = useState(false);
  const [lastSchemaRuleCategory, setLastSchemaRuleCategory] = useState<SchemaRuleCategory>("All");
  const [selectedPreset, setSelectedPreset] = useState<SchemaRulePreset | null>(null);
  const [ruleToEdit, setRuleToEdit] = useState<SchemaRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<SchemaRule | null>(null);

  const totalColumns = columns.length;
  const mandatoryColumns = columns.filter(
    (col) =>
      col.mandatory || rules.some((r) => isMandatoryRule(r) && r.columnIds.includes(col.fieldId))
  ).length;
  const uniqueColumns = columns.filter(
    (col) =>
      col.unique || rules.some((r) => isDuplicatesRule(r) && r.columnIds.includes(col.fieldId))
  ).length;
  const dependencies = rules.reduce(
    (count, rule) => count + Math.max(rule.columnIds.length - 1, 0),
    0
  );
  const duplicateNameColumnIds = findDuplicateNameColumnIds(columns);

  // Overlays the backend hasConflict flag with local-only conflict checks not yet saved.
  const columnFieldIds = new Set([
    ...columns.map((c) => c.fieldId),
    ...(creatingColumnDraft ? [creatingColumnDraft.fieldId] : []),
  ]);
  const referenceListIds = new Set(referenceLists.map((rl) => rl.id));
  const colIdsByName = columnIdsByName(columns);
  const colNamesById = columnNamesById(columns);

  // Groups save conflicts by column for display.
  const conflictGroups = Array.from(
    saveConflicts
      .reduce((groups, r) => {
        const columnName = conflictGroupKey(r, colNamesById);
        const existing = groups.get(columnName) ?? [];
        existing.push(r);
        groups.set(columnName, existing);
        return groups;
      }, new Map<string, SchemaRule[]>())
      .entries()
  ).sort(([a], [b]) => a.localeCompare(b));
  const effectiveRules = rules.map((rule) => {
    const missingReferenceList =
      rule.backendRuleType === "in_reference_list" &&
      typeof rule.parameters?.reference_list_id === "string" &&
      !referenceListIds.has(rule.parameters.reference_list_id);
    return {
      ...rule,
      hasConflict:
        rule.hasConflict ||
        rule.columnIds.some((id) => !columnFieldIds.has(id)) ||
        missingReferenceList ||
        // catches a renamed/deleted column referenced only by name (e.g. x_column) --
        // columnIds alone won't show it since fieldIds survive a rename
        ruleReferencesUnresolvedColumn(rule, colIdsByName),
    };
  });

  const schemaRuleCount = effectiveRules.filter(
    (rule) => (rule.scope ?? "column") === "schema"
  ).length;
  const ruleConflictCount = effectiveRules.filter(
    (r) => (r.scope ?? "column") === "schema" && r.hasConflict
  ).length;

  // Columns tab filter count: distinct columns with a conflict (row-count, not issue-count),
  // matching how many rows "Has Conflicts" actually reveals.
  const conflictingColIds = new Set(
    effectiveRules
      .filter((r) => r.hasConflict && (r.scope ?? "column") === "column")
      .flatMap((r) => r.columnIds)
  );
  duplicateNameColumnIds.forEach((id) => conflictingColIds.add(id));
  const columnConflictCount = columns.filter((c) => conflictingColIds.has(c.fieldId)).length;

  // Schema card/save-payload count: distinct issues, not columns — a column with 2
  // pills contributes 2, so resolving one visibly moves the number.
  const columnScopedRuleConflictCount = effectiveRules.filter(
    (r) => r.hasConflict && (r.scope ?? "column") === "column"
  ).length;
  const conflictCount =
    ruleConflictCount + columnScopedRuleConflictCount + duplicateNameColumnIds.size;

  function createBlankColumn(): SchemaColumn {
    const nextSort = columns.length + 1;

    return {
      fieldId: crypto.randomUUID(),
      id: String(nextSort),
      sortOrder: nextSort,
      name: `Column_${nextSort}`,
      description: "",
      dataType: "General",
      formatting: "---",
      example: "",
      mandatory: false,
      unique: false,
    };
  }

  function requestNavigation(action: () => void) {
    if (isDirtyRef.current) {
      setPendingNavigation(() => action);
      setShowUnsavedWarning(true);
    } else {
      action();
    }
  }

  function buildDraftDescription(): string {
    return (
      editableDescription ||
      (isTemplateSchema && location.state?.templateName
        ? `Customized from ${location.state.templateName}`
        : "")
    );
  }

  function buildDraftPayload(
    description: string,
    allActivity: SchemaActivityEvent[]
  ): SaveDraftPayload {
    const originalCols = originalColumnsRef.current;
    const originalIds = new Set(originalCols.map((c) => c.fieldId));
    const currentIds = new Set(columns.map((c) => c.fieldId));
    const originalRuleIds = new Set(originalRulesRef.current.map((r) => r.id));
    const currentRuleIds = new Set(rules.map((r) => r.id));
    const originalRlIds = new Set(originalReferenceListsRef.current.map((rl) => rl.id));
    const currentRlIds = new Set(
      referenceLists.filter((rl) => !rl.id.startsWith("reference-list-")).map((rl) => rl.id)
    );

    const isNew = !apiSchemaId;

    return {
      schemaId: apiSchemaId ?? null,
      schema: { name: editableName, description, providerId },
      columns: isNew
        ? {
            create: columns.map((col) => ({
              localId: col.fieldId,
              name: col.name || `Column_${col.sortOrder}`,
              dataType: col.dataType,
              mandatory: col.mandatory,
              description: col.description || null,
              position: col.sortOrder - 1,
              formatting: col.formatting || null,
            })),
          }
        : {
            delete: originalCols
              .filter((col) => !currentIds.has(col.fieldId))
              .map((col) => col.fieldId),
            create: columns
              .filter((col) => !originalIds.has(col.fieldId))
              .map((col) => ({
                localId: col.fieldId,
                name: col.name || `Column_${col.sortOrder}`,
                dataType: col.dataType,
                mandatory: col.mandatory,
                description: col.description || null,
                position: col.sortOrder - 1,
                formatting: col.formatting || null,
              })),
            update: columns
              .filter((col) => originalIds.has(col.fieldId))
              .map((col) => ({
                fieldId: col.fieldId,
                name: col.name,
                dataType: col.dataType,
                mandatory: col.mandatory,
                description: col.description || null,
                position: col.sortOrder - 1,
                formatting: col.formatting || null,
              })),
          },
      referenceLists: isNew
        ? {
            create: referenceLists.map((rl) => ({
              localId: rl.id,
              name: rl.name,
              values: rl.values,
            })),
          }
        : {
            delete: originalReferenceListsRef.current
              .filter((rl) => !currentRlIds.has(rl.id))
              .map((rl) => rl.id),
            create: referenceLists
              .filter((rl) => rl.id.startsWith("reference-list-"))
              .map((rl) => ({ localId: rl.id, name: rl.name, values: rl.values })),
            update: referenceLists
              .filter((rl) => originalRlIds.has(rl.id))
              .flatMap((rl) => {
                const orig = originalReferenceListsRef.current.find((o) => o.id === rl.id)!;
                if (orig.name === rl.name && orig.values.join("|") === rl.values.join("|"))
                  return [];
                return [
                  {
                    referenceListId: rl.id,
                    name: rl.name,
                    values: rl.values,
                  },
                ];
              }),
          },
      rules: isNew
        ? {
            create: rules
              .filter((rule) => !isAutoTypeCheckRule(rule))
              .map((rule) => ({
                localId: rule.id,
                ruleTemplateId: rule.presetId ?? rule.backendRuleType ?? rule.id,
                scope: rule.scope ?? "column",
                fieldId: rule.scope === "schema" ? null : (rule.columnIds[0] ?? null),
                parameters: (rule.parameters as Record<string, unknown>) ?? {},
                criticality: rule.criticality ?? null,
                ruleName: rule.ruleKey || null,
                userMessage: rule.description || null,
              })),
          }
        : {
            delete: originalRulesRef.current
              .filter((rule) => !currentRuleIds.has(rule.id))
              .map((rule) => rule.id),
            create: rules
              .filter((rule) => !originalRuleIds.has(rule.id) && !isAutoTypeCheckRule(rule))
              .map((rule) => ({
                localId: rule.id,
                ruleTemplateId: rule.presetId ?? rule.backendRuleType ?? rule.id,
                scope: rule.scope ?? "column",
                fieldId: rule.scope === "schema" ? null : (rule.columnIds[0] ?? null),
                parameters: (rule.parameters as Record<string, unknown>) ?? {},
                criticality: rule.criticality ?? null,
                ruleName: rule.ruleKey || null,
                userMessage: rule.description || null,
              })),
            update: rules
              .filter((rule) => originalRuleIds.has(rule.id) && !isAutoTypeCheckRule(rule))
              .map((rule) => ({
                ruleId: rule.id,
                parameters: (rule.parameters as Record<string, unknown>) ?? undefined,
                criticality: rule.criticality ?? null,
                ruleName: rule.ruleKey || null,
                userMessage: rule.description || null,
              })),
          },
      activity: allActivity.map((e) => ({
        type: e.type,
        actorEmail: e.actor || null,
        detail: e.detail ?? null,
        clientTimestamp: e.timestamp,
      })),
    };
  }

  // A hot-reload or interrupted load can restore the edited columns without the
  // saved baseline; diffing against an empty baseline re-creates every column.
  function baselineLost(): boolean {
    return Boolean(apiSchemaId) && originalColumnsRef.current.length === 0 && columns.length > 0;
  }

  // Applies backend-assigned ids and resets the diff baselines so a later save/validate
  // diffs against the server instead of re-creating everything.
  async function applySavedIdRemap(result: SaveDraftResult): Promise<{
    remappedColumns: SchemaColumn[];
    remappedRules: SchemaRule[];
    warnings: string[];
  }> {
    const targetId = result.schemaId;
    if (!apiSchemaId) setApiSchemaId(targetId);

    // Moves the session key onto the real id once one exists.
    const priorSessionKey = sessionKeyRef.current;
    sessionKeyRef.current = targetId;

    // Drops duplicate columns left by id remapping and re-sequences sortOrder.
    const seenFieldIds = new Set<string>();
    const remappedColumns = columns
      .map((c) => {
        const mapped = result.columnIdMap[c.fieldId];
        return mapped ? { ...c, fieldId: mapped } : c;
      })
      .filter((c) => {
        if (seenFieldIds.has(c.fieldId)) return false;
        seenFieldIds.add(c.fieldId);
        return true;
      })
      .map((c, i) => ({ ...c, sortOrder: i + 1 }));
    let remappedRules = rules.map((r) => {
      const refListId = r.parameters?.reference_list_id;
      const remappedParameters =
        typeof refListId === "string" && result.referenceListIdMap[refListId]
          ? { ...r.parameters, reference_list_id: result.referenceListIdMap[refListId] }
          : r.parameters;
      return {
        ...r,
        id: result.ruleIdMap[r.id] ?? r.id,
        columnIds: r.columnIds.map((cid) => result.columnIdMap[cid] ?? cid),
        parameters: remappedParameters,
      };
    });
    const remappedLists = referenceLists.map((rl) => {
      const mapped = result.referenceListIdMap[rl.id];
      return mapped ? { ...rl, id: mapped } : rl;
    });
    // Refreshes conflict flags from the server so badges and totals update in place.
    try {
      const colNameById = columnNamesById(remappedColumns);
      const refListNameById = Object.fromEntries(remappedLists.map((rl) => [rl.id, rl.name]));
      const fresh = await getSchemaRules(targetId, colNameById, refListNameById);
      const freshById = new Map(fresh.map((r) => [r.id, r]));
      remappedRules = remappedRules.map((r) => {
        const saved = freshById.get(r.id);
        return saved
          ? {
              ...r,
              hasConflict: saved.hasConflict,
              conflictWith: saved.conflictWith,
              conflictReason: saved.conflictReason,
            }
          : r;
      });
    } catch {
      // flags refresh on next load if this read fails
    }
    setColumns(remappedColumns);
    setRules(remappedRules);
    setReferenceLists(remappedLists);
    originalColumnsRef.current = remappedColumns;
    originalRulesRef.current = remappedRules;
    originalReferenceListsRef.current = remappedLists.map((rl) => ({
      id: rl.id,
      name: rl.name,
      values: rl.values,
    }));
    setLocalActivity([]);
    isDirtyRef.current = false;
    if (priorSessionKey) clearEditSession(priorSessionKey);
    clearSchemaActivityLog(targetId);
    deleteCustomSchema(targetId);

    return { remappedColumns, remappedRules, warnings: result.warnings };
  }

  // Saves the draft and validates it against an uploaded sample file in one call.
  async function handleValidateDraft(file: File): Promise<ValidationResult> {
    if (baselineLost()) {
      throw new Error(
        "This editor lost its saved state (the page was reloaded mid-edit). Reload the page before validating."
      );
    }
    const savedEvent: SchemaActivityEvent = {
      id: `local-${Date.now()}`,
      type: "draft_saved",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
    };
    const payload = buildDraftPayload(buildDraftDescription(), [savedEvent, ...localActivity]);
    const result = await saveAndValidate(payload, file);
    const { remappedColumns, remappedRules, warnings } = await applySavedIdRemap(result.save);
    setSaveWarnings(warnings);
    setSaveConflicts(remappedRules.filter((r) => r.hasConflict));
    return mapValidationResult(result.validation, {
      schemaName: editableName,
      fileName: file.name,
      rules: remappedRules,
      columns: remappedColumns,
    });
  }

  async function handlePublishSchema() {
    if (baselineLost()) {
      setSaveError(
        "This editor lost its saved state (the page was reloaded mid-edit). Reload the page before publishing to avoid duplicating columns."
      );
      return;
    }
    setIsPublishing(true);
    setSaveError(null);
    try {
      // Persist latest state first so publish doesn't promote a stale, pre-edit version.
      const savedEvent: SchemaActivityEvent = {
        id: `local-${Date.now()}`,
        type: "draft_saved",
        actor: currentUser?.userEmail ?? "You",
        timestamp: new Date().toISOString(),
      };
      const payload = buildDraftPayload(buildDraftDescription(), [savedEvent, ...localActivity]);
      const result = await saveDraft(payload);
      const { remappedRules, warnings } = await applySavedIdRemap(result);
      const conflictingRules = remappedRules.filter((r) => r.hasConflict);
      await publishSchema(result.schemaId);
      // Backend auto-assigns PSA on publish; drop cached provider lists so Schema
      // Manager / upload pickers see the new assignment after navigation.
      invalidateProvidersCache();
      setSaveWarnings(warnings);
      setSaveConflicts(conflictingRules);
      if (warnings.length > 0 || conflictingRules.length > 0) {
        // Stay put so the warnings/conflicts below are visible instead of navigating past them.
        setIsPublishing(false);
      } else {
        navigate("/schema-builder");
      }
    } catch (err) {
      setSaveError(`Failed to publish: ${(err as Error).message}`);
      setIsPublishing(false);
    }
  }

  async function handleProceed() {
    if (baselineLost()) {
      setSaveError(
        "This editor lost its saved state (the page was reloaded mid-edit). Reload the page before saving to avoid duplicating columns."
      );
      return;
    }

    // Commit any pending name edit first so a just-typed duplicate can't slip through.
    if (isEditingName) {
      const nameOk = await commitNameEdit();
      if (!nameOk) {
        setSaveError("Fix the schema name above before saving — the draft wasn't saved.");
        return;
      }
    } else if (nameError) {
      setSaveError("Fix the schema name above before saving — the draft wasn't saved.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const description = buildDraftDescription();

    const savedEvent: SchemaActivityEvent = {
      id: `local-${Date.now()}`,
      type: "draft_saved",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
    };
    const allActivity = [savedEvent, ...localActivity];

    try {
      const payload = buildDraftPayload(description, allActivity);

      const result = await saveDraft(payload);
      const { remappedColumns, remappedRules, warnings } = await applySavedIdRemap(result);
      const targetId = result.schemaId;

      // Recomputed from the post-save data rather than the stale pre-save conflictCount.
      const conflictingRules = remappedRules.filter((r) => r.hasConflict);
      const freshConflictCount =
        conflictingRules.length + findDuplicateNameColumnIds(remappedColumns).size;

      finalizeNewSchema({
        id: targetId,
        name: editableName,
        description,
        status: "draft",
        totalColumns: remappedColumns.length,
        rules: remappedRules.length,
        dependencies,
        conflicts: freshConflictCount,
        lastEditedAt: "Today",
        lastEditor: currentUser?.userEmail ?? "You",
        createdAt: "Today",
        columns: remappedColumns,
        mappedFiles: [],
      });

      const savedSummary = {
        id: targetId,
        name: editableName,
        description,
        status: "draft" as const,
        totalColumns: remappedColumns.length,
        rules: remappedRules.length,
        dependencies,
        conflicts: freshConflictCount,
        lastEditedAt: "Today",
        lastEditor: currentUser?.userEmail ?? "You",
        createdAt: new Date().toISOString(),
      };
      upsertSchemaInCache(savedSummary);
      saveCustomSchema({ ...savedSummary, columns: remappedColumns, mappedFiles: [] });

      setSaveWarnings(warnings);
      setSaveConflicts(conflictingRules);
      if (warnings.length > 0 || conflictingRules.length > 0) {
        // Stay put so the warnings/conflicts below are visible instead of navigating past them.
      } else {
        navigate("/schema-builder");
      }
    } catch (err) {
      // Preserve activity in localStorage so the next session can recover it
      const targetId = apiSchemaId;
      if (targetId) saveSchemaActivityLog(targetId, allActivity);
      setSaveError(`Failed to save: ${(err as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  }

  function handleMandatoryToggle(fieldId: string) {
    isDirtyRef.current = true;
    const col = columns.find((c) => c.fieldId === fieldId);
    const hasMandatoryRule = rules.some((r) => isMandatoryRule(r) && r.columnIds.includes(fieldId));
    const isCurrentlyMandatory = Boolean(col?.mandatory) || hasMandatoryRule;
    const turningOn = !isCurrentlyMandatory;
    recordLocalActivity({
      type: "column_edited",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: col?.name,
    });
    setColumns((prev) =>
      prev.map((column) =>
        column.fieldId === fieldId ? { ...column, mandatory: turningOn } : column
      )
    );
    if (turningOn) {
      const colName = col?.name ?? fieldId;
      const mandatoryPreset = rulePresets.find((p) => p.backendRuleType === "mandatory");
      // Only add the backend rule if the template supports this column's type; the
      // checkbox itself still toggles regardless.
      if (!mandatoryPreset || !col || mandatoryPreset.applicableDataTypes.includes(col.dataType)) {
        handleAddRule(buildMandatoryRule(fieldId, colName));
      }
    } else {
      // Remove every match, not just the first — legacy/corrupted schemas can stack
      // more than one mandatory rule per column.
      const existingRules = rules.filter(
        (r) => isMandatoryRule(r) && r.columnIds.includes(fieldId)
      );
      existingRules.forEach((rule) => handleDeleteRule(rule));
    }
  }

  function handleUniqueToggle(fieldId: string) {
    isDirtyRef.current = true;
    const col = columns.find((c) => c.fieldId === fieldId);
    const hasDuplicatesRule = rules.some(
      (r) => isDuplicatesRule(r) && r.columnIds.includes(fieldId)
    );
    const isCurrentlyUnique = Boolean(col?.unique) || hasDuplicatesRule;
    const turningOn = !isCurrentlyUnique;
    recordLocalActivity({
      type: "column_edited",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: col?.name,
    });
    setColumns((prev) =>
      prev.map((column) => (column.fieldId === fieldId ? { ...column, unique: turningOn } : column))
    );
    if (turningOn) {
      const colName = col?.name ?? fieldId;
      const uniquePreset = rulePresets.find((p) => p.backendRuleType === "column_unique");
      if (!uniquePreset || !col || uniquePreset.applicableDataTypes.includes(col.dataType)) {
        handleAddRule(buildNoDuplicatesRule(fieldId, colName));
      }
    } else {
      // Remove every match, not just the first — legacy/corrupted schemas can stack
      // more than one duplicates rule per column.
      const existingRules = rules.filter(
        (r) => isDuplicatesRule(r) && r.columnIds.includes(fieldId)
      );
      existingRules.forEach((rule) => handleDeleteRule(rule));
    }
  }

  function handleUpdateColumn(fieldId: string, updates: Partial<SchemaColumn>) {
    isDirtyRef.current = true;
    const col = columns.find((c) => c.fieldId === fieldId);
    recordLocalActivity({
      type: "column_edited",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: col?.name ?? updates.name,
    });

    // Keep cross-column rules pointed at this column through the rename and re-render
    // their displayed formula, instead of going stale until the rule is next saved.
    if (col && updates.name && updates.name !== col.name) {
      const oldName = col.name;
      const newName = updates.name;
      const referenceListNameById = Object.fromEntries(
        referenceLists.map((rl) => [rl.id, rl.name])
      );
      setRules((prev) =>
        prev.map((rule) => {
          const renamed = renameColumnInRuleParameters(rule, oldName, newName);
          if (renamed === rule) return rule;
          const preset = getPresetFromRule(renamed);
          return {
            ...renamed,
            formula: renderRuleFormula(
              preset,
              renamed.parameters ?? {},
              newName,
              referenceListNameById
            ),
          };
        })
      );
    }

    setColumns((prev) => {
      if (updates.sortOrder === undefined) {
        return prev.map((col) => (col.fieldId === fieldId ? { ...col, ...updates } : col));
      }

      const target = prev.find((c) => c.fieldId === fieldId);
      if (!target) {
        return prev.map((col) => (col.fieldId === fieldId ? { ...col, ...updates } : col));
      }

      const oldSort = target.sortOrder;
      const newSort = Math.max(1, Math.min(updates.sortOrder, prev.length));

      return prev
        .map((col) => {
          if (col.fieldId === fieldId) return { ...col, ...updates, sortOrder: newSort };
          if (oldSort < newSort && col.sortOrder > oldSort && col.sortOrder <= newSort)
            return { ...col, sortOrder: col.sortOrder - 1 };
          if (oldSort > newSort && col.sortOrder >= newSort && col.sortOrder < oldSort)
            return { ...col, sortOrder: col.sortOrder + 1 };
          return col;
        })
        .sort((a, b) => a.sortOrder - b.sortOrder);
    });

    setSelectedColumn((prev) =>
      prev && prev.fieldId === fieldId ? { ...prev, ...updates } : prev
    );
  }

  function handleCreateColumn(column: SchemaColumn) {
    isDirtyRef.current = true;
    recordLocalActivity({
      type: "column_added",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: column.name || `Column_${columns.length + 1}`,
    });

    const targetSort = Math.max(1, Math.min(column.sortOrder, columns.length + 1));
    const withCorrectSort = { ...column, sortOrder: targetSort };

    setColumns((prev) => {
      const shifted = prev.map((col) => ({
        ...col,
        sortOrder: col.sortOrder >= targetSort ? col.sortOrder + 1 : col.sortOrder,
      }));
      return [...shifted, withCorrectSort].sort((a, b) => a.sortOrder - b.sortOrder);
    });
    setCreatingColumnDraft(null);
  }

  function handleAddRule(rule: SchemaRule) {
    isDirtyRef.current = true;
    recordLocalActivity({
      type: "rule_added",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: rule.title,
    });
    setRules((prev) => [rule, ...prev]);
  }

  function handleUpdateRule(updatedRule: SchemaRule) {
    isDirtyRef.current = true;
    setRules((prev) => prev.map((rule) => (rule.id === updatedRule.id ? updatedRule : rule)));
  }

  function handleDeleteRule(ruleToRemove: SchemaRule) {
    if (isAutoTypeCheckRule(ruleToRemove)) return;
    isDirtyRef.current = true;
    recordLocalActivity({
      type: "rule_deleted",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: ruleToRemove.title,
    });
    setRules((prev) => prev.filter((rule) => rule.id !== ruleToRemove.id));
  }

  function handleDeleteColumn(fieldId: string) {
    isDirtyRef.current = true;
    const col = columns.find((c) => c.fieldId === fieldId);
    recordLocalActivity({
      type: "column_deleted",
      actor: currentUser?.userEmail ?? "You",
      timestamp: new Date().toISOString(),
      detail: col?.name,
    });

    setColumns((prev) => {
      const deletedSort = prev.find((c) => c.fieldId === fieldId)?.sortOrder ?? prev.length;
      return prev
        .filter((col) => col.fieldId !== fieldId)
        .map((col) => ({
          ...col,
          sortOrder: col.sortOrder > deletedSort ? col.sortOrder - 1 : col.sortOrder,
        }));
    });

    setRules((prev) =>
      prev
        .map((rule) => {
          if (!rule.columnIds.includes(fieldId)) return rule;
          if (rule.columnIds.length === 1) return null;

          return {
            ...rule,
            columnIds: rule.columnIds.filter((id) => id !== fieldId),
            hasConflict: true,
          };
        })
        .filter((rule): rule is SchemaRule => rule !== null)
    );

    setSelectedColumn(null);
  }

  function handleConfirmDeleteColumn() {
    if (!columnToDelete) return;

    handleDeleteColumn(columnToDelete.fieldId);
    setColumnToDelete(null);
  }

  function handleConfirmDeleteRule() {
    if (!ruleToDelete) return;

    handleDeleteRule(ruleToDelete);
    setRuleToDelete(null);
  }

  function handleCreateReferenceList(name: string, values: string[]): string {
    const trimmedName = name.trim();
    const existing = referenceLists.find((list) => list.name === trimmedName);
    const id = existing?.id ?? `reference-list-${crypto.randomUUID()}`;

    setReferenceListValuesByName((prev) => ({
      ...prev,
      [trimmedName]: values,
    }));

    setReferenceLists((prev) => {
      if (existing) {
        return prev.map((list) =>
          list.id === existing.id ? { ...list, itemCount: values.length, values } : list
        );
      }

      return [
        ...prev,
        { id, name: trimmedName, usedInColumnIds: [], itemCount: values.length, values },
      ];
    });

    return id;
  }

  function getRuleColumn(rule: SchemaRule) {
    return columns.find((column) => rule.columnIds.includes(column.fieldId)) ?? columns[0] ?? null;
  }

  function getPresetFromRule(rule: SchemaRule): SchemaRulePreset {
    const preset =
      rulePresets.find((item) => item.id === rule.presetId) ??
      rulePresets.find((item) => item.backendRuleType === rule.backendRuleType) ??
      rulePresets.find((item) => item.title === rule.title);

    if (preset) return preset;

    return {
      id: rule.presetId ?? rule.id,
      backendRuleType: rule.backendRuleType ?? rule.id,
      dqType: rule.dqType ?? "Conditional",
      title: rule.title,
      category: rule.category,
      description: rule.description,
      applicableDataTypes: [],
    };
  }

  function handleOpenSchemaAddRuleFlow() {
    setShowSchemaAddRuleModal(true);
  }

  if (isInitializing) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-[var(--color-text-secondary)]">
        Loading schema…
      </div>
    );
  }

  if (initError) {
    return (
      <div className="px-12 py-8">
        <div className="rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-600">
          {initError}
        </div>
      </div>
    );
  }

  // col is null for the Cross Column Rules tab, which has no anchor column to exclude/match against.
  function buildRuleModalProps(col: (typeof columns)[0] | null) {
    return {
      dateColOpts: toColumnOptions(
        columns.filter((c) => c.dataType === "Date & Time" && c.fieldId !== col?.fieldId)
      ),
      allColOpts: toColumnOptions(columns),
      sameTypeColOpts: toColumnOptions(
        col ? columns.filter((c) => c.dataType === col.dataType) : columns
      ),
      numericColOpts: toColumnOptions(
        columns.filter((c) => c.dataType === "Integer" || c.dataType === "Decimal")
      ),
      colsByName: columnIdsByName(columns),
      refListOpts: referenceLists.map((rl) => ({ id: rl.id, name: rl.name })),
    };
  }

  return (
    <div className="animate-fade-in px-12 py-8">
      {(cameFromEdit || (!cameFromCreate && !cameFromUpload)) && (
        <button
          type="button"
          disabled={isSaving}
          onClick={() => requestNavigation(() => navigate("/schema-builder"))}
          className="mb-8 flex items-center gap-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:text-[var(--color-brand-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)] disabled:hover:text-[var(--color-text-tertiary)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}

      {initWarnings.length > 0 && (
        <div className="mb-6 rounded-md border border-yellow-200 bg-yellow-50 px-5 py-4">
          <p className="mb-2 text-sm font-medium text-yellow-800">
            Some inferred columns or reference lists may need review:
          </p>
          <ul className="list-disc pl-5 text-sm text-yellow-700">
            {initWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className={`mb-10${isSaving ? " pointer-events-none select-none opacity-60" : ""}`}>
        <div className="mb-3 flex items-baseline gap-3">
          {isEditingName ? (
            <input
              autoFocus
              value={nameDraft}
              onChange={(e) => {
                setNameField((prev) => ({ ...prev, draft: e.target.value }));
                setNameError("");
              }}
              onBlur={handleCommitNameEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCommitNameEdit();
                if (e.key === "Escape") {
                  setNameError("");
                  setNameField((prev) => ({ ...prev, draft: prev.value, isEditing: false }));
                }
              }}
              className={`min-w-[420px] max-w-full rounded border bg-white px-2 py-0.5 text-2xl font-semibold text-[var(--color-text-primary)] outline-none ${
                nameError ? "border-red-500" : "border-[var(--color-action-primary)]"
              }`}
            />
          ) : (
            <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
              {editableName}
            </h1>
          )}

          {!isEditingName && (
            <button
              type="button"
              onClick={() =>
                setNameField((prev) => ({ ...prev, draft: prev.value, isEditing: true }))
              }
              className="text-sm text-[var(--color-action-primary)] hover:underline"
            >
              Edit
            </button>
          )}

          {fileName && !isEditingName && (
            <span className="text-sm text-[var(--color-text-secondary)]">{fileName}</span>
          )}

          {isEditingName && isCheckingName && (
            <span className="text-sm text-[var(--color-text-tertiary)]">Checking...</span>
          )}
        </div>

        {nameError && <AlertMessage variant="error" message={nameError} className="mb-3" />}

        {isEditingDescription ? (
          <div className="flex items-start gap-3">
            <textarea
              autoFocus
              value={descriptionDraft}
              onChange={(e) => setDescField((prev) => ({ ...prev, draft: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Escape")
                  setDescField((prev) => ({ ...prev, draft: prev.value, isEditing: false }));
              }}
              rows={3}
              className="flex-1 resize-none rounded border border-[var(--color-action-primary)] bg-white px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none"
            />
            <button
              type="button"
              onClick={handleCommitDescriptionEdit}
              className="mt-1 text-sm text-[var(--color-action-primary)] hover:underline"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <p className="flex-1 text-sm text-[var(--color-text-secondary)]">
              {editableDescription || (
                <span className="italic text-[var(--color-text-tertiary)]">Add description</span>
              )}
            </p>
            <button
              type="button"
              onClick={() =>
                setDescField((prev) => ({ ...prev, draft: prev.value, isEditing: true }))
              }
              className="shrink-0 text-sm text-[var(--color-action-primary)] hover:underline"
            >
              Edit
            </button>
          </div>
        )}

        <div className="mt-8 flex gap-8 border-b border-[var(--color-border-light)]">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              className={`pb-3 text-sm ${
                activeTab === tab.value
                  ? "border-b-2 border-[var(--color-brand-primary)] text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-secondary)]"
              }`}
            >
              {tab.value === "rules"
                ? `Cross Column Rules (${schemaRuleCount})`
                : tab.value === "reference-lists"
                  ? `Reference Lists (${referenceLists.length})`
                  : tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={isSaving ? "pointer-events-none select-none opacity-60" : undefined}>
        {activeTab === "columns" && (
          <SchemaBuilderColumnsTab
            columns={columns}
            rules={effectiveRules}
            totalColumns={totalColumns}
            mandatoryColumns={mandatoryColumns}
            uniqueColumns={uniqueColumns}
            dependencies={dependencies}
            conflictCount={columnConflictCount}
            duplicateNameColumnIds={duplicateNameColumnIds}
            isScratchSchema={isScratchSchema}
            onAddColumn={() => setCreatingColumnDraft(createBlankColumn())}
            onToggleMandatory={handleMandatoryToggle}
            onToggleUnique={handleUniqueToggle}
            onEditColumn={setSelectedColumn}
            onDeleteColumn={setColumnToDelete}
          />
        )}

        {activeTab === "rules" && (
          <SchemaBuilderRulesTab
            rules={effectiveRules}
            conflictCount={ruleConflictCount}
            onAddRule={handleOpenSchemaAddRuleFlow}
            onEditRule={setRuleToEdit}
            onDeleteRule={setRuleToDelete}
          />
        )}

        {activeTab === "reference-lists" && (
          <SchemaBuilderReferenceListsTab
            referenceLists={referenceLists.map((rl) => ({
              ...rl,
              usedInColumnIds: rules
                .filter(
                  (r) =>
                    r.backendRuleType === "in_reference_list" &&
                    r.parameters?.reference_list_id === rl.id
                )
                .flatMap((r) => r.columnIds),
            }))}
            columnNamesById={columnNamesById(columns)}
            onFileUploaded={(fileName) => {
              const now = new Date().toISOString();
              setLocalUploadedFiles((prev) => [
                ...prev,
                {
                  fileName,
                  mappedAt: now,
                  uploadedBy: currentUser?.userEmail ?? "You",
                },
              ]);
              recordLocalActivity({
                type: "file_uploaded",
                actor: currentUser?.userEmail ?? "You",
                timestamp: now,
                detail: fileName,
              });
            }}
            onAddReferenceList={(newList) => {
              isDirtyRef.current = true;
              recordLocalActivity({
                type: "reference_list_added",
                actor: currentUser?.userEmail ?? "You",
                timestamp: new Date().toISOString(),
                detail: newList.name,
              });
              setReferenceLists((prev) => [{ ...newList, usedInColumnIds: [] }, ...prev]);
              setReferenceListValuesByName((prev) => ({
                ...prev,
                [newList.name]: newList.values,
              }));
            }}
            onUpdateReferenceList={(updatedList) => {
              isDirtyRef.current = true;
              recordLocalActivity({
                type: "reference_list_updated",
                actor: currentUser?.userEmail ?? "You",
                timestamp: new Date().toISOString(),
                detail: updatedList.name,
              });
              setReferenceLists((prev) =>
                prev.map((list) => (list.id === updatedList.id ? updatedList : list))
              );
              setReferenceListValuesByName((prev) => {
                const oldName = referenceLists.find((l) => l.id === updatedList.id)?.name;
                const next = { ...prev };
                if (oldName && oldName !== updatedList.name) delete next[oldName];
                next[updatedList.name] = updatedList.values;
                return next;
              });
            }}
            onDeleteReferenceList={(listToDelete) => {
              isDirtyRef.current = true;
              recordLocalActivity({
                type: "reference_list_deleted",
                actor: currentUser?.userEmail ?? "You",
                timestamp: new Date().toISOString(),
                detail: listToDelete.name,
              });
              setReferenceLists((prev) => prev.filter((list) => list.id !== listToDelete.id));
              setReferenceListValuesByName((prev) => {
                const next = { ...prev };
                delete next[listToDelete.name];
                return next;
              });
              // Re-render any rule pointing at this list so its card shows "reference list
              // not found" instead of freezing the raw id into its formula until next save.
              const colNameById = columnNamesById(columns);
              const remainingListNameById = Object.fromEntries(
                referenceLists
                  .filter((rl) => rl.id !== listToDelete.id)
                  .map((rl) => [rl.id, rl.name])
              );
              setRules((prev) =>
                prev.map((rule) => {
                  if (rule.parameters?.reference_list_id !== listToDelete.id) return rule;
                  const preset = getPresetFromRule(rule);
                  return {
                    ...rule,
                    formula: renderRuleFormula(
                      preset,
                      rule.parameters ?? {},
                      (rule.columnIds[0] && colNameById[rule.columnIds[0]]) ?? "",
                      remainingListNameById
                    ),
                  };
                })
              );
            }}
          />
        )}

        {activeTab === "activity" && (
          <SchemaBuilderActivityTab
            schemaId={apiSchemaId}
            schemaName={editableName}
            localActivity={localActivity}
            mappedFiles={localUploadedFiles}
          />
        )}
      </div>

      <div className="mt-12 flex items-center justify-between">
        <div>
          {cameFromUpload && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => requestNavigation(() => navigate("/schema-builder/create/file"))}
              className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)] disabled:hover:text-[var(--color-text-tertiary)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Upload
            </button>
          )}
          {isScratchSchema && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => requestNavigation(() => navigate("/schema-builder/create/scratch"))}
              className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)] disabled:hover:text-[var(--color-text-tertiary)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Setup
            </button>
          )}
          {isTemplateSchema && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => requestNavigation(() => navigate("/schema-builder/create/template"))}
              className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)] disabled:hover:text-[var(--color-text-tertiary)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Template
            </button>
          )}
          {cameFromEdit && (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => requestNavigation(() => navigate("/schema-builder"))}
              className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] transition hover:text-[var(--color-text-primary)] disabled:cursor-not-allowed disabled:text-[var(--color-text-tertiary)] disabled:hover:text-[var(--color-text-tertiary)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Return to Schema List
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={columns.length === 0 || isSaving}
            onClick={() => setShowValidateModal(true)}
            className="rounded-md border border-[var(--color-action-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--color-action-primary)] transition hover:bg-green-50 disabled:cursor-not-allowed disabled:border-[var(--color-border)] disabled:text-[var(--color-text-tertiary)]"
          >
            Validate Draft
          </button>
          <button
            type="button"
            disabled={columns.length === 0 || isSaving}
            onClick={() => void handleProceed()}
            className="rounded-md bg-[var(--color-action-primary)] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--color-action-primary-hover)] disabled:cursor-not-allowed disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Saving...
              </span>
            ) : (
              "Save Draft"
            )}
          </button>
        </div>
      </div>

      {saveError && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {saveError}
        </div>
      )}

      {saveWarnings.length > 0 && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="mb-2 flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Schema saved, but with warnings:
          </p>
          <ul className="space-y-1.5 pl-4">
            {saveWarnings.map((w, i) => (
              <li key={i} className="border-l-2 border-amber-300 pl-2">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {saveConflicts.length > 0 && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="mb-2 flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Schema saved, but these rules conflict:
          </p>
          <ul className="space-y-3">
            {conflictGroups.map(([columnName, groupRules]) => (
              <li key={columnName}>
                {/* Column header is useful for single-column conflicts; skip when it
                    only restates the cross-column pair already shown in the formulas. */}
                {groupRules.every((r) => (r.scope ?? "column") === "column") && (
                  <p className="border-b border-amber-200 pb-1 text-sm font-semibold text-amber-900">
                    {columnName}
                  </p>
                )}
                <ul
                  className={`space-y-1.5 ${
                    groupRules.every((r) => (r.scope ?? "column") === "column") ? "mt-2 pl-4" : ""
                  }`}
                >
                  {clusterConflictingRules(groupRules).map((cluster) => (
                    <li key={cluster.key} className="border-l-2 border-amber-300 pl-2">
                      <ConflictTitles titles={cluster.titles} />
                      <div className="mt-1 text-amber-700">
                        {cluster.reason ?? "Conflicts with another rule."}
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {selectedColumn && (
        <ColumnEditorDrawer
          column={selectedColumn}
          totalColumns={columns.length}
          allColumns={columns}
          rules={effectiveRules}
          referenceLists={referenceLists}
          onAddRule={handleAddRule}
          onUpdateRule={handleUpdateRule}
          onDeleteRule={handleDeleteRule}
          onUpdateColumn={handleUpdateColumn}
          onCreateReferenceList={handleCreateReferenceList}
          onDeleteColumn={handleDeleteColumn}
          onClose={() => setSelectedColumn(null)}
          onFileUploaded={(fileName) =>
            setLocalUploadedFiles((prev) => [
              ...prev,
              {
                fileName,
                mappedAt: new Date().toISOString(),
                uploadedBy: currentUser?.userEmail ?? "You",
              },
            ])
          }
        />
      )}

      {creatingColumnDraft && (
        <ColumnEditorDrawer
          mode="create"
          column={creatingColumnDraft}
          totalColumns={columns.length + 1}
          allColumns={columns}
          rules={effectiveRules}
          referenceLists={referenceLists}
          onAddRule={handleAddRule}
          onUpdateRule={handleUpdateRule}
          onDeleteRule={handleDeleteRule}
          onUpdateColumn={handleUpdateColumn}
          onCreateColumn={handleCreateColumn}
          onCreateReferenceList={handleCreateReferenceList}
          onDeleteColumn={handleDeleteColumn}
          onClose={() => setCreatingColumnDraft(null)}
          onFileUploaded={(fileName) =>
            setLocalUploadedFiles((prev) => [
              ...prev,
              {
                fileName,
                mappedAt: new Date().toISOString(),
                uploadedBy: currentUser?.userEmail ?? "You",
              },
            ])
          }
        />
      )}

      {showSchemaAddRuleModal && (
        <AddRuleModal
          scopeFilter="schema"
          initialCategory={lastSchemaRuleCategory}
          onClose={() => setShowSchemaAddRuleModal(false)}
          onSelectPreset={(preset, category) => {
            setLastSchemaRuleCategory(category);
            setShowSchemaAddRuleModal(false);
            setSelectedPreset(preset);
          }}
        />
      )}

      {selectedPreset &&
        (() => {
          const {
            dateColOpts,
            allColOpts,
            sameTypeColOpts,
            numericColOpts,
            colsByName,
            refListOpts,
          } = buildRuleModalProps(null);
          return (
            <RuleConfigurationModal
              preset={selectedPreset}
              dateColumnOptions={dateColOpts}
              allColumnOptions={allColOpts}
              columnOptions={sameTypeColOpts}
              numericColumnOptions={numericColOpts}
              columnsByName={colsByName}
              existingRules={rules}
              referenceListOptions={refListOpts}
              onCreateReferenceList={handleCreateReferenceList}
              onBack={() => {
                setSelectedPreset(null);
                setShowSchemaAddRuleModal(true);
              }}
              onClose={() => setSelectedPreset(null)}
              onSave={(rule) => {
                handleAddRule(rule);
                setSelectedPreset(null);
                setLastSchemaRuleCategory("All");
              }}
            />
          );
        })()}

      {ruleToEdit &&
        // schema-scoped rules have no anchor column, so allow editing without one
        ((ruleToEdit.scope ?? "column") === "schema" || getRuleColumn(ruleToEdit)) &&
        (() => {
          const isSchemaScoped = (ruleToEdit.scope ?? "column") === "schema";
          const col = isSchemaScoped ? null : getRuleColumn(ruleToEdit)!;
          const {
            dateColOpts,
            allColOpts,
            sameTypeColOpts,
            numericColOpts,
            colsByName,
            refListOpts,
          } = buildRuleModalProps(col);
          return (
            <RuleConfigurationModal
              isEditing
              existingRule={ruleToEdit}
              column={col ?? undefined}
              preset={getPresetFromRule(ruleToEdit)}
              dateColumnOptions={dateColOpts}
              allColumnOptions={allColOpts}
              columnOptions={sameTypeColOpts}
              numericColumnOptions={numericColOpts}
              columnsByName={colsByName}
              existingRules={rules}
              referenceListOptions={refListOpts}
              onCreateReferenceList={handleCreateReferenceList}
              onBack={() => setRuleToEdit(null)}
              onClose={() => setRuleToEdit(null)}
              onSave={(updatedRule) => {
                handleUpdateRule({
                  ...ruleToEdit,
                  ...updatedRule,
                  id: ruleToEdit.id,
                });
                setRuleToEdit(null);
              }}
            />
          );
        })()}

      <ConfirmDialog
        open={showUnsavedWarning}
        onOpenChange={(open) => {
          if (!open) {
            setShowUnsavedWarning(false);
            setPendingNavigation(null);
          }
        }}
        title="Leave without saving?"
        description="Your changes haven't been saved as a draft yet. Click 'Save Draft' to keep your work, or go back and lose it."
        confirmLabel="Go Back"
        cancelLabel="Stay"
        tone="warning"
        onConfirm={() => {
          setShowUnsavedWarning(false);
          pendingNavigation?.();
          setPendingNavigation(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(columnToDelete)}
        onOpenChange={(open) => {
          if (!open) setColumnToDelete(null);
        }}
        title="Delete column?"
        description={
          <>
            This will remove{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {columnToDelete?.name || "this column"}
            </span>{" "}
            from this schema. Rules only linked to this column will be deleted. Rules linked to
            other columns will be marked as conflicts.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmDeleteColumn}
      />

      <ConfirmDialog
        open={Boolean(ruleToDelete)}
        onOpenChange={(open) => {
          if (!open) setRuleToDelete(null);
        }}
        title="Deleting a Rule"
        description={
          <>
            Are you sure you would like to delete{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {ruleToDelete?.ruleKey}
            </span>
            ? Deleting a rule for one column may affect the values of other columns in your schema.
          </>
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmDeleteRule}
      />

      {isPublishing && (
        <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm">
          <Spinner size="lg" />
          <p className="mt-4 text-sm font-medium text-[var(--color-text-secondary)]">
            Publishing schema…
          </p>
        </div>
      )}

      {showValidateModal && (
        <ValidateDraftModal
          onValidate={handleValidateDraft}
          onClose={() => setShowValidateModal(false)}
          canPublish={canPublish}
          saveWarnings={saveWarnings}
          conflictMessages={(() => {
            const messages: string[] = [];
            for (const [, groupRules] of conflictGroups) {
              for (const cluster of clusterConflictingRules(groupRules)) {
                const titles = cluster.titles.join(" conflicts with ");
                messages.push(`${titles}: ${cluster.reason ?? "Conflicts with another rule."}`);
              }
            }
            // Fallback if saveConflicts aren't in conflictGroups yet (shouldn't happen).
            if (messages.length === 0) {
              return saveConflicts.map((r) => {
                const label = conflictDisplayLabel(r);
                return `${label}: ${r.conflictReason ?? "Conflicts with another rule."}`;
              });
            }
            return messages;
          })()}
          onPublish={() => {
            setShowValidateModal(false);
            void handlePublishSchema();
          }}
          onEditRule={(ruleId) => {
            const rule = effectiveRules.find((r) => r.id === ruleId);
            if (!rule) return;
            // Leave the validate modal mounted underneath so closing the rule editor
            // reveals its in-progress results again instead of losing them.
            setRuleToEdit(rule);
          }}
        />
      )}
    </div>
  );
}
